import { DateTime, type DateTime as DateTimeType } from 'luxon';

export const emptyDateFieldCharacter = '--';

export type ParsedTime = {
  dayPeriod: string;
  displayDateFormat: string;
  luxonDateTime: DateTimeType | string;
  time12Hr: string;
  time24Hr: string;
  timeZoneOffset: string;
  timeZoneShortName: string;
};

export const defaultParsedTime: ParsedTime = {
  dayPeriod: emptyDateFieldCharacter,
  displayDateFormat: emptyDateFieldCharacter,
  luxonDateTime: emptyDateFieldCharacter,
  time12Hr: emptyDateFieldCharacter,
  time24Hr: emptyDateFieldCharacter,
  timeZoneOffset: emptyDateFieldCharacter,
  timeZoneShortName: emptyDateFieldCharacter,
};

/**
 * Converts an ISO date string to a UTC Luxon DateTime.
 * @param {string} dateString - The ISO date string.
 * @returns {DateTime} The UTC Luxon DateTime.
 */
export const toUTCLuxonDateTime = (dateString: string): DateTime => {
  return DateTime.fromISO(dateString, {
    setZone: true, // causes offset to override stated zone, and display short names and offsets labeled as UTC instead of GMT
  }).toUTC();
};

/**
 * Converts an ISO date string to a zoned Luxon DateTime.
 * @param {string} dateString - The ISO date string.
 * @param {string} timeZone - The time zone.
 * @returns {DateTime} The zoned Luxon DateTime.
 */
export const toZonedLuxonDateTime = (dateString: string, timeZone: string): DateTime => {
  return DateTime.fromISO(dateString, {
    zone: timeZone,
    // 'setZone: true' causes offset to override stated zone, and display short names and offsets labeled as UTC instead of GMT
  });
};

/**
 * Parses a Luxon DateTime to its parts.
 * @param {DateTimeType} luxonDateTime - The Luxon DateTime.
 * @returns {ParsedTime} The parsed time parts.
 */
export const parseLuxonDateToParts = (
  luxonDateTime: DateTimeType, // optionally Zoned, assumptions will be made if not zoned
): ParsedTime => {
  if (luxonDateTime) {
    const time12Hr = luxonDateTime.toFormat('h:mm');
    const dayPeriod = luxonDateTime.toFormat('a');
    const time24Hr = luxonDateTime.toFormat('H:mm');
    const displayDateFormat = luxonDateTime.toFormat('LLL d y');
    const timeZoneOffset = `GMT${luxonDateTime.toFormat('Z')}`;
    const timeZoneShortName = luxonDateTime.toFormat('ZZZZ');

    return {
      luxonDateTime,
      time12Hr,
      dayPeriod,
      time24Hr,
      displayDateFormat,
      timeZoneOffset,
      timeZoneShortName,
    };
  } else {
    return defaultParsedTime;
  }
};

type UnitDisplay = 'long' | 'short' | 'narrow';
type DurationUnit =
  | 'years'
  | 'quarters'
  | 'months'
  | 'weeks'
  | 'days'
  | 'hours'
  | 'minutes'
  | 'seconds'
  | 'milliseconds';

interface RenderDifferenceBetweenOpts {
  units?: DurationUnit[];
  floorVals?: boolean;
  drop0DurationUnits?: boolean;
  allowNegative?: boolean;
  unitDisplay?: UnitDisplay;
}

/**
 * Renders the duration between two Luxon DateTime objects.
 * @param {DateTimeType} from - The start date-time.
 * @param {DateTimeType} to - The end date-time.
 * @param {RenderDifferenceBetweenOpts} [opts] - The options for rendering the duration.
 * @returns {string} The rendered duration.
 */
export const renderDuration = (
  from: DateTimeType,
  to: DateTimeType,
  opts?: RenderDifferenceBetweenOpts,
): string => {
  const coalescedOpts = {
    units: ['years', 'months', 'days', 'hours', 'minutes', 'seconds'] as DurationUnit[],
    floorVals: true,
    drop0DurationUnits: true,
    allowNegative: false,
    unitDisplay: 'short' as UnitDisplay,
    ...opts,
  };
  if (
    to &&
    from &&
    (coalescedOpts.allowNegative || (!coalescedOpts.allowNegative && to > from))
  ) {
    let difference = to.diff(from, coalescedOpts.units);

    if (coalescedOpts.floorVals) {
      difference = difference.mapUnits(Math.floor);
    }

    if (coalescedOpts.drop0DurationUnits) {
      const durationObj = difference.toObject();
      const optionalFloor = coalescedOpts.floorVals ? Math.floor : (num: number) => num;
      const non0Parts = Object.entries(durationObj)
        // remove any parts that ended up being 0-duration when floored
        .filter(entry => !!optionalFloor(parseInt(entry[1] as string)))
        // convert the part names back to an array to be used for formatting
        .map(entry => entry[0] as DurationUnit);
      if (!non0Parts.length) {
        return '';
      } else {
        // spread the parts array back in to shiftTo so only those parts are shown
        difference = difference.shiftTo(...non0Parts);
      }
    }

    const humanReadableDifference = difference.toHuman({
      unitDisplay: coalescedOpts.unitDisplay,
    });

    // if duration is < smallest unit and floor is true, this may be empty string
    return humanReadableDifference;
  } else {
    return '';
  }
};